import * as React           from "react";
import {cx}                 from "emotion";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {faCheck}            from "@fortawesome/free-solid-svg-icons";

import {equalDeep}          from "@Common/utils/objects";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {scopedState}        from "@Common/react/scoped-state";
import {UseState}           from "@Common/react/use-state";
import {PasswordInput}      from "@Common/ui-components/password-input/password-input";
import {setNestedValue}     from "@Common/utils/arr-path";
import {UserNameEdit}       from "@Xpresso/routes/account/common/user-name-edit/user-name-edit";
import {MenuLayout}         from "../menu-layout/menu-layout";

import "./security-menu.scss";

export const SecurityMenu = ({user, readOnly, dataUpdated}) => cs(
    consumeContext("apis"),
    consumeContext("company"),
    ["uploading", (_, next) => UseState({next})],
    ["securityState", (_, next) => user.value && UseState({
        _key: "security",
        next,
        initValue: defaultState({user})
    })],
    ["validation", ({securityState}, next) => next({
        length: securityState.value?.password?.length >= 6,
        number: /\d/.test(securityState.value?.password),
        lowercase: securityState.value?.password && /[a-z]/.test(securityState.value?.password),
        uppercase: /[A-Z]/.test(securityState.value?.password),
        special: /[@$!%*#?&]/.test(securityState.value?.password),
        matches: securityState.value?.password && securityState.value?.password === securityState.value?.matchPassword,
        full: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/.test(securityState.value?.password) && securityState.value?.password === securityState.value?.matchPassword
    })],
    ["validationLines", ({validation}, next) => next(getValidationLines({validation}))],
    ["editPassword", ({}, next) => UseState({
        next,
        initValue: false
    })],
    ({apis, uploading, securityState, validation}, next) => MenuLayout({
        title: "Security",
        button: null,
        next,
    }),
    ({apis, editPassword, securityState, uploading, validation, validationLines}) => (
        <form className="security-menu-f8d">
            {UserNameEdit({user, readOnly: readOnly?.inactive || readOnly?.companyAdmin})}
            {!(readOnly?.inactive || readOnly?.companyAdmin) &&
                <div className="section">
                    <div className="header">
                        <div className="title">
                            Password
                        </div>
                        <a className="edit" xd-qa-type="link" xd-qa-name={editPassword.value ? "Cancel Manage Password" : "Edit Manage Password"} 
                            onClick={() => {
                                const toggle = !editPassword.value
                                editPassword.onChange(toggle)
                            }}>
                            {editPassword.value ? "Cancel" : "Edit"}
                        </a>
                    </div>
                </div>
            }           
            <div className={cx("password-edit", {visible: editPassword.value})}>
                <div className="input-card">
                    <div className="label" xd-qa-type="label" xd-qa-name="Enter a New Password">Enter a New Password</div>
                    {PasswordInput({inputProps:{
                        readOnly: readOnly?.inactive || readOnly?.companyAdmin,
                        value: scopedState(securityState, ["password"]).value,
                        onChange: (e) => {
                            let value = e.target.value;
                            scopedState(securityState, ["password"]).onChange(value);
                            dataUpdated.onChange(!equalDeep(defaultState({user}), setNestedValue(securityState.value, ['password'], value)))
                        },
                        disabled: readOnly?.inactive || readOnly?.companyAdmin,
                        className: cx("value", {"read-only": readOnly?.inactive}),
                        maxLength: 64,
                        "mapping": "password",
                        "xd-qa-name": "Enter a New Password",
                        autoComplete: "new-password",
                    }})}
                </div>
                <div className="validation-card">
                    {validationLines.map((line, i) => (
                        <div className="validation-line" key={i}>
                            {line.validated ? (
                                <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name={line.message + " Check"}/></div>
                            ) : (
                                <div className="validation-spacer"/>
                            )}
                            <div className={cx("validation-label", {validated: line.validated})} xd-qa-type="label" xd-qa-name={line.message}>{line.message}</div>
                        </div>
                    ))}
                </div>
                <div className="input-card">
                    <div className="label" xd-qa-type="label" xd-qa-name="Confirm New Password">Confirm New Password</div>
                    {PasswordInput({inputProps:{
                        readOnly: readOnly?.inactive || readOnly?.companyAdmin,
                        value: scopedState(securityState, ["matchPassword"]).value,
                        onChange: (e) => {
                            let value = e.target.value; 
                            scopedState(securityState, ["matchPassword"]).onChange(value);
                            dataUpdated.onChange(!equalDeep(defaultState({user}), setNestedValue(securityState.value, ['matchPassword'], value)))
                        },
                        disabled: readOnly?.inactive || readOnly?.companyAdmin,
                        className: cx("value", {"read-only": readOnly?.inactive}),
                        maxLength: 64,
                        "xd-qa-name": "Confirm New Password",
                        autoComplete: "new-password",
                    }})}
                </div>
                <div className="validation-card">
                    <div className="validation-line">
                        {validation.matches ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Passwords Match Check"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.matches})} xd-qa-type="label" xd-qa-name="Passwords Match">Passwords Match</div>
                    </div>
                </div>
                <div className={cx("password-save",
                    {disabled: readOnly?.inactive || uploading.value || !validation.full}
                )}
                    onClick={ async () => {
                        if(readOnly?.inactive || uploading.value || !validation.full) {
                            return
                        }
                        uploading.onChange(true);
                        const result = await apis.next.userProfile.updateUserProfile({userId: user.value.id, password: securityState.value.password});
                        if (result?.errorMessage) {
                            alert(result.errorMessage);
                        } else if (result?.errorInfo) {
                            const errors = JSON.parse(result.errorInfo);
                            errors.map((error) => {
                                scopedState(securityState, ['errors', error.mapping]).onChange(error.message)
                            });
                        } else {
                            dataUpdated.onChange(false)
                            await user.reload()
                            securityState.onChange({password: '', matchPassword: '', userId: user.value.userId})
                            editPassword.onChange(false)
                        }
                        uploading.onChange(false)
                    }}
                    xd-qa-type="button"
                    xd-qa-name="Save Password"
                >
                    Save
                </div>
            </div>
        </form>
    )
);

const defaultState = ({user}) => {
    let inputs = {
        password: '',
        matchPassword: '',
        userId: user.value.id
    }
    return inputs
}

const getValidationLines = ({validation}) => ([
    {
        "message": "At least 6 characters", 
        "validated": validation.length, 
    },
    {
        "message": "At least 1 number", 
        "validated": validation.number, 
    },
    {
        "message": "At least 1 lowercase letter", 
        "validated": validation.lowercase, 
    },
    {
        "message": "At least 1 uppercase letter", 
        "validated": validation.uppercase, 
    },
    {
        "message": "At least 1 special character @$!%*#?&", 
        "validated": validation.special, 
    },
]);